import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState, type FormEvent } from "react";
import {
  listenbrainzApi,
  type AddSeedRequest,
  type DerivedArtist,
  type ExpansionPreview,
  type SeedSummary,
} from "@/api/listenbrainz";
import { musicApi, type ArtistLookupResult } from "@/api/music";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Checkbox, Field, NumberInput, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";

// React Query key namespace. Anything table-related lives under "lb-seeds"
// so a single invalidate refreshes the page.
const SEEDS_KEY = ["lb-seeds"] as const;

const DEFAULT_CONFIG: AddSeedConfig = {
  max_similar: 25,
  max_albums: 5,
  include_eps: false,
};

interface AddSeedConfig {
  max_similar: number;
  max_albums: number;
  include_eps: boolean;
}

export function Discover() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [derivedFor, setDerivedFor] = useState<SeedSummary | null>(null);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const seedsQ = useQuery({
    queryKey: SEEDS_KEY,
    queryFn: listenbrainzApi.seeds.list,
    // Newly-derived artists trickle in async on the backend; auto-refresh so
    // the row counts reflect progress without the user having to click around.
    refetchInterval: 30_000,
  });

  const refresh = async (seed: SeedSummary) => {
    setRefreshingId(seed.id);
    try {
      const res = await listenbrainzApi.seeds.refresh(seed.id);
      const parts: string[] = [];
      if (res.artists_added > 0)
        parts.push(`${res.artists_added} new artists`);
      if (res.albums_queued > 0)
        parts.push(`${res.albums_queued} albums queued`);
      if (res.artists_skipped_already_present > 0)
        parts.push(`${res.artists_skipped_already_present} already present`);
      if (res.failures.length > 0)
        parts.push(`${res.failures.length} failed`);
      toast(
        parts.length === 0
          ? `${seed.artist_name}: nothing new`
          : `${seed.artist_name}: ${parts.join(", ")}`,
        res.failures.length > 0 ? "info" : "success",
      );
      qc.invalidateQueries({ queryKey: SEEDS_KEY });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Refresh failed", "error");
    } finally {
      setRefreshingId(null);
    }
  };

  const remove = async (seed: SeedSummary) => {
    if (
      !confirm(
        `Delete the "${seed.artist_name}" seed? Already-derived artists and queued albums stay — this only stops future expansions.`,
      )
    ) {
      return;
    }
    setRemovingId(seed.id);
    try {
      await listenbrainzApi.seeds.remove(seed.id);
      toast(`Removed seed "${seed.artist_name}"`, "info");
      qc.invalidateQueries({ queryKey: SEEDS_KEY });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed", "error");
    } finally {
      setRemovingId(null);
    }
  };

  const seeds = seedsQ.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Discover"
        description="Add seed artists; arr-in-one expands each via ListenBrainz to similar artists and queues their albums."
        actions={<Button onClick={() => setAdding(true)}>Add seed</Button>}
      />

      {seedsQ.isLoading ? (
        <p className="text-sm text-foreground/60">Loading seeds…</p>
      ) : seedsQ.isError ? (
        <EmptyState
          title="Couldn't load seeds"
          description={
            seedsQ.error instanceof Error
              ? seedsQ.error.message
              : "Backend returned an error."
          }
          action={
            <Button variant="secondary" onClick={() => seedsQ.refetch()}>
              Retry
            </Button>
          }
        />
      ) : seeds.length === 0 ? (
        <EmptyState
          title="No seeds yet"
          description="Pick an artist you like. arr-in-one will find similar artists via ListenBrainz and queue their albums."
          action={<Button onClick={() => setAdding(true)}>Add seed</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-3 py-2 font-medium">Seed artist</th>
                <th className="px-3 py-2 font-medium">Config</th>
                <th className="px-3 py-2 font-medium">Derived</th>
                <th className="px-3 py-2 font-medium">Last expanded</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {seeds.map((s) => (
                <tr key={s.id} className="border-t border-line">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium">{s.artist_name}</div>
                    <div className="font-mono text-[10px] text-foreground/40">
                      {s.artist_mbid}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-foreground/70">
                    {describeConfig(s)}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-foreground/70 tabular-nums">
                    <span className="text-foreground">
                      {s.derived_artists_count}
                    </span>{" "}
                    artists ·{" "}
                    <span className="text-foreground">
                      {s.derived_albums_count}
                    </span>{" "}
                    albums
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-foreground/60">
                    {s.last_expanded_at
                      ? formatRelative(s.last_expanded_at)
                      : "never"}
                  </td>
                  <td className="space-x-2 px-3 py-2 text-right align-top">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={refreshingId === s.id}
                      onClick={() => refresh(s)}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDerivedFor(s)}
                    >
                      View derived
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={removingId === s.id}
                      onClick={() => remove(s)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddSeedModal open={adding} onClose={() => setAdding(false)} />
      <DerivedModal seed={derivedFor} onClose={() => setDerivedFor(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-seed modal — three-step flow
// ---------------------------------------------------------------------------

type AddSeedState =
  | {
      mode: "search";
      query: string;
      results: ArtistLookupResult[] | null;
      loading: boolean;
    }
  | {
      mode: "configure";
      artist: ArtistLookupResult;
      config: AddSeedConfig;
    }
  | {
      mode: "preview";
      artist: ArtistLookupResult;
      config: AddSeedConfig;
      preview: ExpansionPreview;
    };

const INITIAL_STATE: AddSeedState = {
  mode: "search",
  query: "",
  results: null,
  loading: false,
};

function AddSeedModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<AddSeedState>(INITIAL_STATE);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const closeAndReset = () => {
    onClose();
    // Defer reset so the user doesn't see the modal contents flash on
    // close-then-reopen.
    window.setTimeout(() => setState(INITIAL_STATE), 200);
  };

  // ---- Step 1: search -----------------------------------------------------
  const search = async (e?: FormEvent) => {
    e?.preventDefault();
    if (state.mode !== "search") return;
    const q = state.query.trim();
    if (!q) return;
    setState({ ...state, loading: true });
    try {
      const hits = await musicApi.lookupArtists(q);
      setState({
        mode: "search",
        query: q,
        results: hits.slice(0, 10),
        loading: false,
      });
    } catch (e) {
      setState((s) => (s.mode === "search" ? { ...s, loading: false } : s));
      toast(e instanceof Error ? e.message : "Lookup failed", "error");
    }
  };

  const pickArtist = (artist: ArtistLookupResult) => {
    setState({ mode: "configure", artist, config: { ...DEFAULT_CONFIG } });
  };

  // ---- Step 2: configure → preview ----------------------------------------
  const goPreview = async () => {
    if (state.mode !== "configure") return;
    const body: AddSeedRequest = {
      artist_mbid: state.artist.mbid,
      max_similar: state.config.max_similar,
      max_albums: state.config.max_albums,
      include_eps: state.config.include_eps,
    };
    setPreviewing(true);
    try {
      const preview = await listenbrainzApi.preview(body);
      setState({
        mode: "preview",
        artist: state.artist,
        config: state.config,
        preview,
      });
    } catch (e) {
      // Preview is read-only; surface the error and keep the user on the
      // Configure step so they can adjust and retry. Common cases: upstream
      // ListenBrainz failure (5xx) or an unknown MBID.
      const msg =
        e instanceof ApiError
          ? `Preview failed (HTTP ${e.status}): ${e.message}`
          : e instanceof Error
            ? `Preview failed: ${e.message}`
            : "Preview failed";
      toast(msg, "error");
    } finally {
      setPreviewing(false);
    }
  };

  // ---- Step 3: confirm ----------------------------------------------------
  const submit = async () => {
    if (state.mode !== "preview") return;
    const body: AddSeedRequest = {
      artist_mbid: state.artist.mbid,
      max_similar: state.config.max_similar,
      max_albums: state.config.max_albums,
      include_eps: state.config.include_eps,
    };
    setSubmitting(true);
    try {
      await listenbrainzApi.seeds.add(body);
      toast(
        `Seed added — queueing ${state.preview.estimated_albums} albums in the background`,
        "success",
      );
      qc.invalidateQueries({ queryKey: SEEDS_KEY });
      closeAndReset();
    } catch (e) {
      // 409 = the seed already exists (same MBID). Refresh the table so the
      // user sees it, then close — no point making them retry.
      if (e instanceof ApiError && e.status === 409) {
        toast(
          `"${state.artist.name}" is already a seed`,
          "info",
        );
        qc.invalidateQueries({ queryKey: SEEDS_KEY });
        closeAndReset();
        return;
      }
      toast(e instanceof Error ? e.message : "Add failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render -------------------------------------------------------------
  const title =
    state.mode === "search"
      ? "Add seed — search for an artist"
      : state.mode === "configure"
        ? "Add seed — configure"
        : "Add seed — preview";

  return (
    <Modal open={open} onClose={closeAndReset} title={title} width="lg">
      {state.mode === "search" && (
        <SearchStep
          state={state}
          onChangeQuery={(query) =>
            setState({ ...state, query, results: state.results })
          }
          onSearch={search}
          onPick={pickArtist}
        />
      )}
      {state.mode === "configure" && (
        <ConfigureStep
          artist={state.artist}
          config={state.config}
          previewing={previewing}
          onChange={(config) =>
            setState({ mode: "configure", artist: state.artist, config })
          }
          onChangeArtist={() => setState(INITIAL_STATE)}
          onPreview={goPreview}
          onCancel={closeAndReset}
        />
      )}
      {state.mode === "preview" && (
        <PreviewStep
          artist={state.artist}
          preview={state.preview}
          config={state.config}
          submitting={submitting}
          onBack={() =>
            setState({
              mode: "configure",
              artist: state.artist,
              config: state.config,
            })
          }
          onSubmit={submit}
          onCancel={closeAndReset}
        />
      )}
    </Modal>
  );
}

// ---- Step components -------------------------------------------------------

function SearchStep({
  state,
  onChangeQuery,
  onSearch,
  onPick,
}: {
  state: Extract<AddSeedState, { mode: "search" }>;
  onChangeQuery: (q: string) => void;
  onSearch: (e?: FormEvent) => void;
  onPick: (a: ArtistLookupResult) => void;
}) {
  return (
    <div className="space-y-4">
      <form onSubmit={onSearch}>
        <Field label="Search MusicBrainz">
          <div className="flex gap-2">
            <TextInput
              autoFocus
              value={state.query}
              onChange={(e) => onChangeQuery(e.target.value)}
              placeholder="Billy Strings"
            />
            <Button type="submit" loading={state.loading}>
              Search
            </Button>
          </div>
        </Field>
      </form>

      {state.results !== null && state.results.length > 0 && (
        <div className="max-h-96 space-y-2 overflow-y-auto pr-2">
          {state.results.map((r) => (
            <button
              key={r.mbid}
              type="button"
              onClick={() => onPick(r)}
              className="flex w-full items-start justify-between gap-3 rounded border border-line bg-surface/40 p-3 text-left transition hover:border-accent"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{r.name}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground/60">
                  {r.disambiguation && <span>{r.disambiguation}</span>}
                </div>
                <div className="mt-1 truncate font-mono text-[10px] text-foreground/40">
                  {r.mbid}
                </div>
              </div>
              <span className="shrink-0 self-center text-xs text-accent">
                Select →
              </span>
            </button>
          ))}
        </div>
      )}

      {state.results !== null &&
        state.results.length === 0 &&
        !state.loading && (
          <p className="text-sm text-foreground/60">
            No results — try a different query.
          </p>
        )}
    </div>
  );
}

function ConfigureStep({
  artist,
  config,
  previewing,
  onChange,
  onChangeArtist,
  onPreview,
  onCancel,
}: {
  artist: ArtistLookupResult;
  config: AddSeedConfig;
  previewing: boolean;
  onChange: (config: AddSeedConfig) => void;
  onChangeArtist: () => void;
  onPreview: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-4">
      <ArtistBanner artist={artist} onChange={onChangeArtist} />

      <Field
        label="Max similar artists"
        hint="Range 0–100. Default 25. Set to 0 to import the seed artist only."
      >
        <NumberInput
          min={0}
          max={100}
          value={config.max_similar}
          onChange={(e) => {
            // Empty string from the input parses to NaN — fall back to 0
            // (the "seed-only" choice) rather than silently bumping back
            // to 1, which used to mask the issue.
            const raw = parseInt(e.target.value, 10);
            const n = Number.isNaN(raw) ? 0 : raw;
            onChange({
              ...config,
              max_similar: clamp(n, 0, 100),
            });
          }}
        />
      </Field>

      <Field label="Max albums per artist" hint="Range 1–20. Default 5.">
        <NumberInput
          min={1}
          max={20}
          value={config.max_albums}
          onChange={(e) =>
            onChange({
              ...config,
              max_albums: clamp(parseInt(e.target.value, 10) || 1, 1, 20),
            })
          }
        />
      </Field>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox
          checked={config.include_eps}
          onChange={(e) =>
            onChange({ ...config, include_eps: e.target.checked })
          }
        />
        <span>Include EPs and singles</span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onPreview} loading={previewing}>
          Preview
        </Button>
      </div>
    </div>
  );
}

function PreviewStep({
  artist,
  preview,
  config,
  submitting,
  onBack,
  onSubmit,
  onCancel,
}: {
  artist: ArtistLookupResult;
  preview: ExpansionPreview;
  config: AddSeedConfig;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const newArtists =
    preview.similar_artists_count -
    Math.min(preview.already_in_library, preview.similar_artists_count);
  // Distinguish between "user explicitly chose seed-only" (max_similar=0)
  // and "upstream genuinely returned no similar artists" — both surface as
  // similar_artists_count=0 from the backend, but they mean different
  // things to the user.
  const isSeedOnly = config.max_similar === 0;
  const upstreamEmpty = !isSeedOnly && preview.similar_artists_count === 0;
  return (
    <div className="space-y-4">
      <ArtistBanner artist={artist} />

      {isSeedOnly ? (
        <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm">
          <strong className="text-foreground">Seed-only import.</strong>{" "}
          <span className="text-foreground/60">
            Similar-artist discovery is off (max similar = 0). We'll only
            import {artist.name}'s own albums.
          </span>
        </div>
      ) : upstreamEmpty ? (
        <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm">
          <strong className="text-foreground">
            No similar artists found.
          </strong>{" "}
          <span className="text-foreground/60">
            ListenBrainz didn't return any similar artists for {artist.name}.
            Adding the seed will still import {artist.name}'s own albums.
          </span>
        </div>
      ) : (
        <div className="rounded border border-line bg-surface/40 px-4 py-3 text-sm">
          <strong className="text-foreground">
            {preview.similar_artists_count} similar artists
          </strong>
          , ~
          <strong className="text-foreground">
            {preview.estimated_albums} albums
          </strong>{" "}
          (
          <strong className="text-foreground">
            {preview.already_in_library} already in library
          </strong>
          ).{" "}
          <span className="text-foreground/60">
            {newArtists} new artist{newArtists === 1 ? "" : "s"} will be added.
          </span>
        </div>
      )}

      {preview.sample.length > 0 && (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-3 py-2 font-medium">Artist</th>
                <th className="px-3 py-2 font-medium tabular-nums">Score</th>
                <th className="px-3 py-2 font-medium tabular-nums">Albums</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {preview.sample.map((a) => (
                <tr key={a.mbid} className="border-t border-line">
                  <td className="px-3 py-2 font-medium">{a.name}</td>
                  <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                    {a.similarity_score.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                    {a.album_count_in_mb}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.already_in_library && (
                      <span className="rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] uppercase text-green-300">
                        in library
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview.similar_artists_count > preview.sample.length && (
        <p className="text-xs text-foreground/50">
          Showing top {preview.sample.length} by similarity. The full expansion
          will include {preview.similar_artists_count} artists.
        </p>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-line pt-3">
        <Button variant="secondary" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={submitting}>
            Add and queue
          </Button>
        </div>
      </div>
    </div>
  );
}

function ArtistBanner({
  artist,
  onChange,
}: {
  artist: ArtistLookupResult;
  onChange?: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded border border-line bg-surface/40 px-4 py-3">
      <div className="min-w-0">
        <div className="font-medium">{artist.name}</div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground/60">
          {artist.disambiguation && <span>{artist.disambiguation}</span>}
        </div>
        <div className="mt-1 truncate font-mono text-[10px] text-foreground/40">
          {artist.mbid}
        </div>
      </div>
      {onChange && (
        <Button variant="ghost" size="sm" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View Derived modal
// ---------------------------------------------------------------------------

type DerivedSort = "score" | "name" | "albums";

function DerivedModal({
  seed,
  onClose,
}: {
  seed: SeedSummary | null;
  onClose: () => void;
}) {
  const [sort, setSort] = useState<DerivedSort>("score");

  const derivedQ = useQuery({
    queryKey: ["lb-derived", seed?.id],
    queryFn: () => listenbrainzApi.seeds.derived(seed!.id),
    enabled: !!seed,
  });

  const sorted = useMemo(() => {
    const rows = derivedQ.data ?? [];
    const copy = rows.slice();
    if (sort === "score") {
      copy.sort(
        (a, b) => (b.similarity_score ?? -1) - (a.similarity_score ?? -1),
      );
    } else if (sort === "name") {
      copy.sort((a, b) => a.artist_name.localeCompare(b.artist_name));
    } else {
      copy.sort((a, b) => b.albums_added - a.albums_added);
    }
    return copy;
  }, [derivedQ.data, sort]);

  return (
    <Modal
      open={!!seed}
      onClose={onClose}
      title={
        seed ? `Derived from "${seed.artist_name}"` : "Derived"
      }
      width="lg"
    >
      {derivedQ.isLoading ? (
        <p className="text-sm text-foreground/60">Loading derived artists…</p>
      ) : derivedQ.isError ? (
        <p className="text-sm text-red-300">
          {derivedQ.error instanceof Error
            ? derivedQ.error.message
            : "Couldn't load derived artists."}
        </p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-foreground/60">
          No derived artists yet. Click Refresh on the seed to expand.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    <SortHeader
                      label="Artist"
                      active={sort === "name"}
                      onClick={() => setSort("name")}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium tabular-nums">
                    <SortHeader
                      label="Score"
                      active={sort === "score"}
                      onClick={() => setSort("score")}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium tabular-nums">
                    <SortHeader
                      label="Albums"
                      active={sort === "albums"}
                      onClick={() => setSort("albums")}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Derived</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d) => (
                  <DerivedRow key={d.artist_mbid} d={d} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SortHeader({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "uppercase tracking-wide",
        active ? "text-foreground" : "text-foreground/60 hover:text-foreground",
      )}
    >
      {label}
      {active && <span className="ml-1 text-foreground/40">▼</span>}
    </button>
  );
}

function DerivedRow({ d }: { d: DerivedArtist }) {
  // Derived artists may or may not have a local artist row yet (one is
  // created when an album for them gets queued). When `artist_id` is
  // present, link into the library detail page; otherwise fall back to
  // plain text.
  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2">
        {d.artist_id != null ? (
          <Link
            to="/music/artists/$id"
            params={{ id: String(d.artist_id) }}
            className="font-medium hover:text-accent"
          >
            {d.artist_name}
          </Link>
        ) : (
          <div className="font-medium">{d.artist_name}</div>
        )}
        <div className="font-mono text-[10px] text-foreground/40">
          {d.artist_mbid}
        </div>
      </td>
      <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
        {d.similarity_score === null ? "—" : d.similarity_score.toFixed(1)}
      </td>
      <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
        {d.albums_added}
      </td>
      <td className="px-3 py-2 text-xs text-foreground/60">
        {formatRelative(d.derived_at)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function describeConfig(s: SeedSummary): string {
  const parts = [
    `≤${s.max_similar} similar`,
    `≤${s.max_albums} albums each`,
    s.include_eps ? "incl. EPs" : "no EPs",
  ];
  return parts.join(" · ");
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

/** "5m ago" / "2h ago" / "yesterday" / "Mar 12" — best-effort relative date. */
function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
